import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { useT } from "@/i18n";
import { framesBoardMessages } from "@/i18n/messages/frames-board";
import { cn } from "@/lib/cn";
import { Spinner } from "@/components/ui";
import { useFrameSurface } from "../shared/use-frame-surface";

interface BoardCard {
  id: string;
  title: string;
  meta?: string;
}

interface BoardColumn {
  id: string;
  label: string;
  dotColor: string;
  count?: number;
  cards: BoardCard[];
}

function toBoardColumn(raw: unknown, index: number): BoardColumn | null {
  if (typeof raw !== "object" || raw === null) return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.label !== "string") return null;
  const rawCards = Array.isArray(c.cards) ? c.cards : [];
  const cards: BoardCard[] = [];
  rawCards.forEach((rawCard, cardIndex) => {
    if (typeof rawCard !== "object" || rawCard === null) return;
    const card = rawCard as Record<string, unknown>;
    if (typeof card.title !== "string") return;
    cards.push({
      id: typeof card.id === "string" ? card.id : `card-${index}-${cardIndex}`,
      title: card.title,
      meta: typeof card.meta === "string" ? card.meta : undefined,
    });
  });
  return {
    id: typeof c.id === "string" ? c.id : `column-${index}`,
    label: c.label,
    dotColor: typeof c.dotColor === "string" ? c.dotColor : "bg-muted",
    count: typeof c.count === "number" ? c.count : undefined,
    cards,
  };
}

/**
 * Production BoardFrame — read-only mini board snapshot (header + compact
 * kanban columns).
 *
 * Column/card recipe copied from the workshop board-frame (column dots,
 * left priority border, title + muted meta line). Cards are read-only
 * divs: no onCardClick, no follow-up prompts — production chrome provides
 * follow-ups. No FrameHeader/FrameFooter: canvas drag uses the hover chrome
 * drag zone, and comments/resize/delete come free via the wrap() HOC
 * chain + hover chrome. Content renders from data.content
 * ({title, path, columns}); agent frame.update broadcasts re-render
 * automatically.
 */
export function BoardFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesBoardMessages);
  const frameSurface = useFrameSurface(id);
  const content = data.content ?? {};
  const title = (content.title as string | undefined) ?? "";
  const path =
    (content.path as string | undefined) ??
    (content.subtitle as string | undefined) ??
    "";
  const rawColumns = Array.isArray(content.columns)
    ? content.columns
    : undefined;
  const columns =
    rawColumns !== undefined
      ? rawColumns
          .map((raw, index) => toBoardColumn(raw, index))
          .filter((c): c is BoardColumn => c !== null)
      : [];
  const status = data.status;

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="board-frame"
      {...frameSurface.attributes}
    >
      <div
        className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-hidden p-4 [--denk-frame-content-padding-block-start:16px] [--denk-frame-content-padding-block-end:16px] [--denk-frame-content-padding-inline:16px]"
        data-frame-content="true"
        data-frame-drag-handle="true"
        onClick={(e) => e.stopPropagation()}
      >
        {(title || path || columns.length > 0) && (
          <div
            className="flex items-center gap-2"
            data-testid="board-frame-header"
          >
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-appkit-body font-semibold leading-snug text-primary"
                data-testid="board-frame-title"
              >
                {title}
              </p>
              {path && (
                <p
                  className="mt-0.5 truncate text-appkit-small text-muted"
                  data-testid="board-frame-path"
                >
                  {path}
                </p>
              )}
            </div>
            <span
              className="max-w-[40%] shrink-0 truncate rounded-pill px-2 py-0.5 text-appkit-small font-medium text-secondary"
              data-testid="board-frame-kind-pill"
            >
              {t("kindPill")}
            </span>
          </div>
        )}

        {status === "streaming" && columns.length === 0 ? (
          <div
            className="flex items-center gap-2 py-3"
            data-testid="board-frame-loading"
            aria-label={t("loadingBoard")}
          >
            <Spinner className="h-3 w-3" />
            <span className="text-appkit-body text-muted">
              {t("loadingBoardEllipsis")}
            </span>
          </div>
        ) : columns.length === 0 ? (
          <FrameEmptyState
            frameId={id}
            status={status}
            testId="board-frame-empty"
          >
            {t("noColumns")}
          </FrameEmptyState>
        ) : (
          <div
            className="nodrag flex min-h-0 flex-1 gap-2 overflow-auto"
            data-testid="board-frame-columns"
            onClick={(e) => e.stopPropagation()}
          >
            {columns.map((column) => {
              const count = column.count ?? column.cards.length;
              return (
                <div
                  key={column.id}
                  className="flex w-[150px] shrink-0 flex-col gap-1.5"
                  data-testid={`board-frame-column-${column.id}`}
                >
                  <div
                    className="flex items-center gap-1.5"
                    data-testid={`board-frame-column-header-${column.id}`}
                  >
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        column.dotColor,
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate text-appkit-small font-medium text-secondary">
                      {column.label}
                    </span>
                    <span className="shrink-0 font-mono text-appkit-small text-muted">
                      {count}
                    </span>
                  </div>

                  {column.cards.length === 0 ? (
                    <p
                      className="rounded-control px-2 py-1.5 text-center text-appkit-small italic text-muted"
                      data-testid={`board-frame-column-empty-${column.id}`}
                    >
                      {t("noCards")}
                    </p>
                  ) : (
                    column.cards.map((card) => (
                      <div
                        key={card.id}
                        data-testid={`board-frame-card-${card.id}`}
                        className="rounded-control px-2 py-1.5"
                      >
                        <p className="truncate text-appkit-small font-medium leading-snug text-primary">
                          {card.title}
                        </p>
                        {card.meta && (
                          <p className="mt-0.5 truncate text-appkit-small text-muted">
                            {card.meta}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
